import { Component, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { SharedModule } from '@apps/landing/src/app/shared/shared.module';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageCardComponent } from '@apps/landing/src/app/shared/components/cards/image-card/image-card.component';
import { InfoCardComponent } from '@apps/landing/src/app/shared/components/cards/info-card/info-card.component';
import { CustomCarouselComponent } from '@apps/landing/src/app/shared/components/custom-carousel/custom-carousel.component';
import { EmployeesService } from '@apps/landing/src/app/modules/about-us/services/employees.service';
import { forkJoin } from 'rxjs';
import { PAGE_ROUTES } from '@apps/landing/src/app/shared/constants/routes.enum';
import { mockProject } from '@apps/landing/src/app/modules/services/utils/constants';
import { IEmployeeModel } from '@apps/landing/src/app/modules/about-us/models/employee.model';

@Component({
  selector: 'app-project-details',
  imports: [SharedModule, ImageCardComponent, InfoCardComponent, CustomCarouselComponent],
  standalone: true,
  templateUrl: './project-details.component.html',
})
export class ProjectDetailsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly employeesService = inject(EmployeesService);

  protected readonly mockProject = mockProject;

  protected project: any;
  filteredEmployees = signal<IEmployeeModel[]>([]);

  ngOnInit(): void {
    const idFromRoute = this.route.snapshot.paramMap.get('id');
    if (idFromRoute) {
      this.project = this.mockProject.find((project: any) => project.id === +idFromRoute);
      this.loadEmployees();
    } else {
      this.router.navigate([PAGE_ROUTES.SERVICES]);
    }
  }

  private loadEmployees(): void {
    const developerIds = this.project?.developers?.developerIds;

    if (!developerIds?.length) return;

    const requests = developerIds.map((id: string) => this.employeesService.getEmployeeById(id));

    forkJoin(requests).subscribe({
      next: (response: any) => {
        const enrichedEmployees = response.map((employee: any) => {
          const mockTemplate = this.employeesService.employeeMockData;

          return {
            ...mockTemplate,
            ...employee.data,
            fullName: `${employee.data.firstName} ${employee.data.lastName}`,
            image: employee.data.gender?.toLowerCase() === 'female' ? 'assets/media/images/user3.svg' : 'assets/media/images/user2.svg',
          };
        });

        this.filteredEmployees.set(enrichedEmployees);
      },
    });
  }

  protected goToEmployee(employee: IEmployeeModel) {
    this.router.navigate([PAGE_ROUTES.ABOUT_US, employee.id]);
  }

  // for swiping
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

  private isDown = false;
  private startX = 0;
  private scrollLeft = 0;

  ngAfterViewInit() {
    const slider = this.scrollContainer.nativeElement;

    slider.addEventListener('mousedown', e => {
      this.isDown = true;
      slider.classList.add('cursor-grabbing');
      this.startX = e.pageX - slider.offsetLeft;
      this.scrollLeft = slider.scrollLeft;
    });
    slider.addEventListener('mouseleave', () => {
      this.isDown = false;
      slider.classList.remove('cursor-grabbing');
    });
    slider.addEventListener('mouseup', () => {
      this.isDown = false;
      slider.classList.remove('cursor-grabbing');
    });
    slider.addEventListener('mousemove', e => {
      if (!this.isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - this.startX) * 2; // scroll-fast multiplier
      slider.scrollLeft = this.scrollLeft - walk;
    });

    // Touch events for mobile
    slider.addEventListener('touchstart', e => {
      this.isDown = true;
      this.startX = e.touches[0].pageX - slider.offsetLeft;
      this.scrollLeft = slider.scrollLeft;
    });
    slider.addEventListener('touchend', () => {
      this.isDown = false;
    });
    slider.addEventListener('touchmove', e => {
      if (!this.isDown) return;
      const x = e.touches[0].pageX - slider.offsetLeft;
      const walk = (x - this.startX) * 2;
      slider.scrollLeft = this.scrollLeft - walk;
    });
  }
}
